/**
 * CreateTaskPage — admin creates a new task and assigns it to a member
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { createTask, getApprovedTMUsers, getMyProjects, getAllProjects } from '@/services/tmService';
import type { TMUser, TaskPriority, TMProject } from '@/types/tm.types';
import { TASK_PRIORITY_LABELS, TASK_PRIORITIES } from '@/types/tm.types';

const schema = z.object({
  title:         z.string().min(1, 'Title is required').max(200),
  description:   z.string().max(2000).optional(),
  assignedToUid: z.string().optional(),
  projectId:     z.string().optional(),
  priority:      z.enum(['low', 'medium', 'high', 'urgent']),
  dueDate:       z.string().optional(),
  reminderDays:  z.coerce.number().min(0).max(30).default(1),
  tags:          z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export default function CreateTaskPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { tmUser, isTMAdmin } = useTMAuth();
  const [members,  setMembers]  = useState<TMUser[]>([]);
  const [projects, setProjects] = useState<TMProject[]>([]);

  // Pre-select project if coming from ProjectDetailPage
  const defaultProjectId = searchParams.get('projectId') ?? '';

  useEffect(() => {
    getApprovedTMUsers().then(setMembers);
    if (isTMAdmin) {
      getAllProjects().then(p => setProjects(p.filter(pr => pr.status === 'active')));
    } else if (tmUser) {
      getMyProjects(tmUser.uid).then(p => setProjects(p.filter(pr => pr.status === 'active')));
    }
  }, [tmUser, isTMAdmin]);

  const { register, handleSubmit, control, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: { priority: 'medium', reminderDays: 1, assignedToUid: '', projectId: defaultProjectId },
    });

  const onSubmit = async (v: FormValues) => {
    if (!tmUser) return;
    try {
      const assignee = members.find(m => m.uid === v.assignedToUid);
      const projectId = v.projectId || null;
      await createTask({
        projectId,
        title:        v.title,
        description:  v.description || null,
        assignedTo:   assignee ? { uid: assignee.uid, name: assignee.name, email: assignee.email, phone: assignee.phone } : null,
        assignedBy:   { uid: tmUser.uid, name: tmUser.name, email: tmUser.email },
        priority:     v.priority as TaskPriority,
        dueDate:      v.dueDate ? new Date(v.dueDate) : null,
        reminderDays: v.reminderDays,
        tags:         v.tags ? v.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      toast.success('Task created!');
      // Return to project detail if we came from there, otherwise task list
      navigate(projectId ? `/tm/projects/${projectId}` : '/tm/tasks');
    } catch {
      toast.error('Failed to create task.');
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">New Task</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">

        {/* Title */}
        <div>
          <label className={labelCls}>Title <span className="text-red-500">*</span></label>
          <input placeholder="What needs to be done?" className={inputCls} {...register('title')} />
          {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>}
        </div>

        {/* Description */}
        <div>
          <label className={labelCls}>Description</label>
          <textarea rows={4} placeholder="Add details, instructions, links…" className={inputCls}
            {...register('description')} />
        </div>

        {/* Assign to */}
        <div>
          <label className={labelCls}>Assign to</label>
          <select className={inputCls} {...register('assignedToUid')}>
            <option value="">Unassigned</option>
            {members.map(m => (
              <option key={m.uid} value={m.uid}>{m.name} ({m.email})</option>
            ))}
          </select>
        </div>

        {/* Project — always render; Controller keeps value in sync even when options load async */}
        <div>
          <label className={labelCls}>Project</label>
          <Controller
            control={control}
            name="projectId"
            render={({ field }) => (
              <select {...field} className={inputCls}>
                <option value="">No project (general task)</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          />
          {defaultProjectId && projects.length === 0 && (
            <p className="text-xs text-amber-500 mt-1">Loading project…</p>
          )}
          <p className="text-xs text-gray-400 mt-1">Only project members can view tasks inside a project.</p>
        </div>

        {/* Priority */}
        <div>
          <label className={labelCls}>Priority</label>
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <div className="flex gap-2 flex-wrap">
                {TASK_PRIORITIES.map(p => (
                  <button key={p} type="button"
                    onClick={() => field.onChange(p)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all capitalize ${
                      field.value === p
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}>
                    {TASK_PRIORITY_LABELS[p]}
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        {/* Due date */}
        <div>
          <label className={labelCls}>Due Date</label>
          <input type="date" className={inputCls}
            min={new Date().toISOString().split('T')[0]}
            {...register('dueDate')} />
        </div>

        {/* Reminder */}
        <div>
          <label className={labelCls}>Email reminder (days before due)</label>
          <input type="number" min={0} max={30} className={inputCls} {...register('reminderDays')} />
          <p className="text-xs text-gray-400 mt-1">Set to 0 to disable reminders</p>
        </div>

        {/* Tags */}
        <div>
          <label className={labelCls}>Tags (comma-separated)</label>
          <input placeholder="e.g. urgent, follow-up, design" className={inputCls} {...register('tags')} />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm
                     hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-60"
        >
          {isSubmitting ? 'Creating…' : 'Create Task'}
        </button>
      </form>
    </div>
  );
}
